'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { ensurePostHogInitialized } from '@/lib/client-analytics';

const LANDING_PAGE_SESSION_KEY = 'posthog_landing_page_tracked';
const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
] as const;

const SOCIAL_HOST_MATCHERS = [
  'facebook.',
  'instagram.',
  'linkedin.',
  'twitter.',
  'x.com',
  't.co',
  'reddit.',
  'tiktok.',
];
const SEARCH_HOST_MATCHERS = ['google.', 'bing.', 'duckduckgo.', 'yahoo.', 'ecosia.', 'brave.com'];
const AI_HOST_MATCHERS = ['chatgpt.com', 'openai.com', 'perplexity.ai', 'claude.ai', 'gemini.google.com'];

type TrafficProperties = Record<string, boolean | number | string | undefined>;

function cleanProperties(properties: TrafficProperties) {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== undefined)) as Record<
    string,
    boolean | number | string
  >;
}

function matchesKnownHost(host: string, matchers: string[]) {
  return matchers.some(matcher => host.includes(matcher));
}

function getPageCategory(pathname: string) {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/chat')) return 'chat';
  if (pathname.startsWith('/auth')) return 'auth';
  if (pathname.startsWith('/pricing')) return 'pricing';
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname.startsWith('/docs')) return 'docs';
  if (pathname.startsWith('/api')) return 'api';
  return 'site';
}

function getAttributionProperties(searchParams: URLSearchParams) {
  return ATTRIBUTION_KEYS.reduce<TrafficProperties>((properties, key) => {
    const value = searchParams.get(key);
    if (value) {
      properties[key] = value;
    }
    return properties;
  }, {});
}

function getReferrerHost() {
  if (typeof document === 'undefined' || !document.referrer) {
    return undefined;
  }

  try {
    return new URL(document.referrer).hostname;
  } catch {
    return undefined;
  }
}

function inferEntryChannel({
  referrerHost,
  utmMedium,
  hasClickId,
}: {
  referrerHost?: string;
  utmMedium?: string;
  hasClickId: boolean;
}) {
  const normalizedMedium = utmMedium?.toLowerCase();
  const normalizedReferrerHost = referrerHost?.toLowerCase();

  if (hasClickId || ['cpc', 'ppc', 'paid', 'paid_social', 'paid-social', 'display'].includes(normalizedMedium || '')) {
    return 'paid';
  }

  if (normalizedMedium === 'email') {
    return 'email';
  }

  if (normalizedReferrerHost && matchesKnownHost(normalizedReferrerHost, AI_HOST_MATCHERS)) {
    return 'ai_referral';
  }

  if (
    normalizedMedium === 'social' ||
    normalizedMedium === 'social_media' ||
    normalizedMedium === 'social-media' ||
    (normalizedReferrerHost && matchesKnownHost(normalizedReferrerHost, SOCIAL_HOST_MATCHERS))
  ) {
    return 'social';
  }

  if (
    normalizedMedium === 'organic' ||
    normalizedMedium === 'seo' ||
    (normalizedReferrerHost && matchesKnownHost(normalizedReferrerHost, SEARCH_HOST_MATCHERS))
  ) {
    return 'search';
  }

  if (normalizedReferrerHost) {
    return 'referral';
  }

  return 'direct';
}

export function PostHogTrafficTracking() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedRouteRef = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || !ensurePostHogInitialized() || typeof window === 'undefined') {
      return;
    }

    const queryString = searchParams?.toString() || '';
    const routeKey = queryString ? `${pathname}?${queryString}` : pathname;

    if (lastTrackedRouteRef.current === routeKey) {
      return;
    }
    lastTrackedRouteRef.current = routeKey;

    const urlSearchParams = new URLSearchParams(queryString);
    const attributionProperties = getAttributionProperties(urlSearchParams);
    const referrerHost = getReferrerHost();
    const pageCategory = getPageCategory(pathname);
    const hasClickId = Boolean(
      attributionProperties.gclid ||
      attributionProperties.fbclid ||
      attributionProperties.msclkid ||
      attributionProperties.ttclid
    );
    const entryChannel = inferEntryChannel({
      referrerHost,
      utmMedium: typeof attributionProperties.utm_medium === 'string' ? attributionProperties.utm_medium : undefined,
      hasClickId,
    });

    const sharedProperties = cleanProperties({
      page_path: pathname,
      page_category: pageCategory,
      page_title: document.title || undefined,
      entry_referrer_host: referrerHost,
      entry_channel: entryChannel,
      has_attribution_params: Object.keys(attributionProperties).length > 0,
      ...attributionProperties,
    });

    posthog.register_once(
      cleanProperties({
        initial_landing_path: pathname,
        initial_page_category: pageCategory,
        initial_referrer_host: referrerHost || 'direct',
        initial_entry_channel: entryChannel,
        initial_utm_source: attributionProperties.utm_source as string | undefined,
        initial_utm_medium: attributionProperties.utm_medium as string | undefined,
        initial_utm_campaign: attributionProperties.utm_campaign as string | undefined,
      })
    );

    posthog.capture('$pageview', sharedProperties);

    if (!window.sessionStorage.getItem(LANDING_PAGE_SESSION_KEY)) {
      window.sessionStorage.setItem(LANDING_PAGE_SESSION_KEY, routeKey);
      posthog.capture(
        'site_landing_page_viewed',
        cleanProperties({
          ...sharedProperties,
          landing_page_path: pathname,
        })
      );
    }
  }, [pathname, searchParams]);

  return null;
}
